import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Percent, Tag, Calendar, Globe, FileText, Gift, Palette, ShoppingBag } from "lucide-react";
import { formatDistanceToNow, isPast } from "date-fns";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { getFormattedDateTime } from "@/utils";

interface OfferData {
  id: string;
  name: string;
  description: string;
  discountType: string;
  discountValue: string;
  status: string;
  startDate?: string;
  endDate?: string;
  bannerImage?: string;
  offerUrl?: string;
  category?: string;
  terms?: string;
  imageUri?: string;
  title?: string;
  eligiblePurchase?: string;
  colorCode?: string;
  fullOfferData?: any; // Full API response
}

interface ViewOfferModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  offer: OfferData | null;
  children?: React.ReactNode;
}

export function ViewOfferModal({
  open,
  onOpenChange,
  offer,
  children,
}: ViewOfferModalProps) {
  if (!offer) return null;

  // Check if offer is expired
  const isExpired = offer.endDate && isPast(new Date(offer.endDate));
  const effectiveStatus = isExpired ? "Expired" : offer.status;

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return "";
    return getFormattedDateTime(dateString) || dateString;
  };

  const formatDateFromMillis = (millis?: number) => {
    if (!millis) return "";
    return getFormattedDateTime(millis);
  };

  const fullOffer = offer.fullOfferData;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            {fullOffer?.title || offer?.title || offer?.name || "Discount Details"}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh] overflow-auto pr-4">
          <div className="space-y-6 py-4">
            {/* Discount Image */}
            <div className="w-full h-48 overflow-hidden rounded-lg bg-gray-100 flex items-center justify-center">
              <img
                src={
                  fullOffer?.imageUri || offer?.imageUri || offer?.bannerImage || "/placeholder.svg"
                }
                alt={fullOffer?.title || offer?.title || offer?.name || "Discount"}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold mb-2">
                  {fullOffer?.title || offer?.name || "Discount"}
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge
                    variant={effectiveStatus === "Active" ? "default" : "destructive"}
                    className={
                      effectiveStatus === "Active"
                        ? "bg-green-500 hover:bg-green-600"
                        : ""
                    }
                  >
                    {fullOffer?.isActive !== undefined
                      ? (fullOffer.isActive ? "Active" : "Inactive")
                      : effectiveStatus}
                  </Badge>
                  {fullOffer?.categoryVO?.categoryName && (
                    <Badge variant="outline">
                      {fullOffer.categoryVO.categoryName}
                    </Badge>
                  )}
                  {fullOffer?.cashbackPercentage !== undefined && (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Percent className="h-3 w-3" />
                      {fullOffer.cashbackPercentage}% Cashback
                    </Badge>
                  )}
                </div>
              </div>

              {/* Description */}
              {(fullOffer?.description || offer?.description) && (
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-md bg-blue-50 text-blue-600 shrink-0">
                    <Tag className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">Description</p>
                    <p className="text-sm text-muted-foreground">
                      {fullOffer?.description || offer?.description}
                    </p>
                  </div>
                </div>
              )}

              {/* Offer ID */}
              {fullOffer?.id && (
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-md bg-gray-50 text-gray-600 shrink-0">
                    <FileText className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">Discount ID</p>
                    <p className="text-sm text-muted-foreground">{fullOffer.id}</p>
                  </div>
                </div>
              )}

              {/* Cashback Percentage */}
              {fullOffer?.cashbackPercentage !== undefined && (
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-md bg-green-50 text-green-600 shrink-0">
                    <Percent className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">Cashback Percentage</p>
                    <p className="text-sm text-muted-foreground">
                      {fullOffer.cashbackPercentage}%
                    </p>
                  </div>
                </div>
              )}

              {/* Category */}
              {fullOffer?.categoryVO && (
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-md bg-purple-50 text-purple-600 shrink-0">
                    <ShoppingBag className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">Category</p>
                    <div className="text-sm text-muted-foreground">
                      <p>Name: {fullOffer.categoryVO.categoryName || "N/A"}</p>
                      {fullOffer.categoryVO.categoryCode && (
                        <p>Code: {fullOffer.categoryVO.categoryCode}</p>
                      )}
                      {fullOffer.categoryVO.cashbackPercentage !== undefined && (
                        <p>Category Cashback: {fullOffer.categoryVO.cashbackPercentage}%</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Dates */}
              {(fullOffer?.startDateInMillis || fullOffer?.endDateInMillis || offer?.startDate || offer?.endDate) && (
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-md bg-orange-50 text-orange-600 shrink-0">
                    <Calendar className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">Validity Period</p>
                    <div className="text-sm text-muted-foreground space-y-1">
                      {fullOffer?.startDateInMillis && (
                        <p>
                          <span className="font-medium">Start:</span>{" "}
                          {formatDateFromMillis(fullOffer.startDateInMillis)}
                        </p>
                      )}
                      {fullOffer?.endDateInMillis && (
                        <p>
                          <span className="font-medium">End:</span>{" "}
                          {formatDateFromMillis(fullOffer.endDateInMillis)}
                        </p>
                      )}
                      {!fullOffer && offer?.startDate && (
                        <p>
                          <span className="font-medium">Start:</span>{" "}
                          {formatDateTime(offer.startDate)}
                        </p>
                      )}
                      {!fullOffer && offer?.endDate && (
                        <p>
                          <span className="font-medium">End:</span>{" "}
                          {formatDateTime(offer.endDate)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Offer URL */}
              {(fullOffer?.offerURL || offer?.offerUrl) && (
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-md bg-blue-50 text-blue-600 shrink-0">
                    <Globe className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">Discount URL</p>
                    <a
                      href={fullOffer?.offerURL || offer?.offerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline break-all"
                    >
                      {fullOffer?.offerURL || offer?.offerUrl}
                    </a>
                  </div>
                </div>
              )}

              {/* Eligible Purchase */}
              {(fullOffer?.eligiblePurchase || offer?.eligiblePurchase) && (
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-md bg-indigo-50 text-indigo-600 shrink-0">
                    <ShoppingBag className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">Discount Eligibility</p>
                    <p className="text-sm text-muted-foreground">
                      {fullOffer?.eligiblePurchase || offer?.eligiblePurchase}
                    </p>
                  </div>
                </div>
              )}

              {/* Color Code */}
              {(fullOffer?.colorCode || offer?.colorCode) && (
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-md bg-pink-50 text-pink-600 shrink-0">
                    <Palette className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">Color Code</p>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded border border-gray-300"
                        style={{
                          backgroundColor: fullOffer?.colorCode || offer?.colorCode,
                        }}
                      />
                      <span className="text-sm text-muted-foreground">
                        {fullOffer?.colorCode || offer?.colorCode}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Terms & Conditions */}
              {(fullOffer?.termsAndConditions || offer?.terms) && (
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-md bg-blue-50 text-blue-600 shrink-0">
                    <FileText className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">Terms & Conditions</p>
                    {fullOffer?.termsAndConditions && /<[a-z][\s\S]*>/i.test(fullOffer.termsAndConditions) ? (
                      <div
                        className="text-xs text-muted-foreground whitespace-pre-wrap"
                        dangerouslySetInnerHTML={{ __html: fullOffer.termsAndConditions }}
                      />
                    ) : (
                      <p className="text-xs text-muted-foreground whitespace-pre-wrap">
                        {fullOffer?.termsAndConditions || offer?.terms}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
